import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import dayjs from "@/lib/bizday";
import api, { apiErr } from "@/lib/api";
import { inr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PlusCircle, Search, Trash2, AlertTriangle, CheckCircle2, Receipt } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";

const DEPT = { vet: "Veterinary", veterinary: "Veterinary", clinic: "Veterinary", grooming: "Grooming", spa: "Grooming", boarding: "Boarding", daycare: "Daycare", training: "Training", diagnostics: "Diagnostics", swimming: "Swimming" };
const deptLabel = (d) => DEPT[(d || "").toLowerCase()] || (d ? d[0].toUpperCase() + d.slice(1) : "Other");
const ROW_STATUS = { scheduled: "Scheduled", in_progress: "In progress", completed: "Completed", cancelled: "Cancelled" };
const t = (s) => dayjs(s).format("D MMM, h:mm A");
const ist = (v) => (v ? `${v}:00+05:30` : "");
const OPEN = ["pending", "confirmed", "in_progress"];
const invalidate = (qc) => ["bookings", "assignments", "cal", "staff-avail", "vet-assignment-issues", "vet-my-queue", "dashboard", "invoices"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));

export function AddServiceButton({ booking, compact }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  if (!OPEN.includes(booking.status) || !hasPerm(user, "bookings.edit")) return null;
  return (
    <>
      <button title="Add service" data-testid={`add-service-btn-${booking.id}`} onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-1.5 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-50 ${compact ? "p-2" : ""}`}>
        <PlusCircle className="h-4 w-4" />{!compact && "Add service"}
      </button>
      {open && <AddServiceDialog booking={booking} onClose={() => setOpen(false)} />}
    </>
  );
}

export function UnbilledChip({ booking }) {
  const u = booking.billing?.unbilled;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  if (!u) return null;
  const bill = async () => {
    setBusy(true);
    try {
      const r = (await api.post(`/bookings/${booking.id}/bill-added-services`)).data;
      toast.success(`Invoice ${r.invoice_number} updated — new total ${inr(r.total)}`);
      invalidate(qc);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "NEEDS_CORRECTION") toast.error(d.msg, { action: { label: "Open invoices", onClick: () => navigate(`/invoices?search=${encodeURIComponent(d.invoice_number || "")}`) } });
      else toast.error(apiErr(e));
    } finally { setBusy(false); }
  };
  return (
    <button onClick={bill} disabled={busy} data-testid={`unbilled-chip-${booking.id}`} title={`Added after billing: ${u.names.join(", ")}`}
      className="mt-1 inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 hover:bg-amber-200">
      <Receipt className="h-3 w-3" />{busy ? "Updating…" : `+${inr(u.total)} not on invoice`}
    </button>
  );
}

function Catalogue({ services, picked, onBooking, onPick }) {
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return services.filter((x) => x.active !== false && (!s || `${x.name} ${x.category}`.toLowerCase().includes(s)));
  }, [services, q]);
  return (
    <div>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search services…" className="h-10 bg-white pl-8" data-testid="add-svc-search" />
      </div>
      <div className="mt-2 max-h-48 space-y-1 overflow-y-auto rounded-lg border border-slate-100 p-1" data-testid="add-svc-catalogue">
        {!list.length && <p className="px-2 py-2 text-xs text-slate-400">No matching services.</p>}
        {list.map((s) => (
          <button key={s.id} type="button" onClick={() => onPick(s)} data-testid={`add-svc-option-${s.id}`}
            className={`flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors hover:bg-slate-50 ${picked.has(s.id) ? "bg-emerald-50" : ""}`}>
            <span className="min-w-0 break-words"><span className="font-medium text-slate-800">{s.name}</span>
              <span className="ml-1.5 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">{deptLabel(s.category)}</span>
              {onBooking.has(s.id) && <span className="ml-1 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700" data-testid={`add-svc-already-${s.id}`}>Already on booking</span>}</span>
            <span className="shrink-0 text-xs text-slate-500">{inr(s.price)} · {s.duration || 60}m</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function StaffSelect({ item, line, branchId, onChange }) {
  const ok = line?.start_time && line?.end_time;
  const { data } = useQuery({ queryKey: ["staff-avail-add", item.service_id, line?.start_time, line?.end_time, branchId], enabled: !!ok,
    queryFn: async () => (await api.get(`/staff/availability?start=${encodeURIComponent(line.start_time)}&end=${encodeURIComponent(line.end_time)}&service_id=${item.service_id}&category=${line.category || ""}&only_eligible=true&branch_id=${branchId || ""}`)).data });
  if (line && !line.start_time) return <p className="text-xs text-slate-500">Stay service — no staff time reserved.</p>;
  const items = data?.items || [];
  return (
    <div>
      <Label className="text-xs">Staff</Label>
      <select value={item.staff_id} onChange={(e) => onChange(e.target.value)} className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid={`add-svc-staff-${item.key}`}>
        <option value="">Unassigned (assign later)</option>
        {items.map((x) => <option key={x.id} value={x.id}>{x.name} ({x.role}){x.available ? "" : x.unavailable ? " — unavailable" : " — busy"}</option>)}
      </select>
      {data && !items.length && <p className="mt-1 text-xs text-amber-700" data-testid={`add-svc-staff-none-${item.key}`}>No eligible staff for this service — it can stay unassigned.</p>}
    </div>
  );
}

function ItemCard({ item, line, pets, branchId, onChange, onRemove }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`add-svc-item-${item.key}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="break-words font-semibold text-slate-900">{item.name}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
            <span className="rounded bg-sky-50 px-1.5 py-0.5 font-semibold text-sky-700" data-testid={`add-svc-dept-${item.key}`}>{deptLabel(line?.department || item.category)} department</span>
            {line?.start_time && <span data-testid={`add-svc-time-${item.key}`}>{t(line.start_time)} – {dayjs(line.end_time).format("h:mm A")}</span>}
          </div>
        </div>
        <button onClick={onRemove} className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600" data-testid={`add-svc-remove-${item.key}`}><Trash2 className="h-4 w-4" /></button>
      </div>
      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {pets.length > 1 && (
          <div><Label className="text-xs">Pet</Label>
            <select value={item.pet_id} onChange={(e) => onChange({ pet_id: e.target.value })} className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid={`add-svc-pet-${item.key}`}>
              {pets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select></div>
        )}
        <div><Label className="text-xs">Start (leave blank for next free slot)</Label>
          <Input type="datetime-local" value={item.start} onChange={(e) => onChange({ start: e.target.value })} className="mt-1 h-10" data-testid={`add-svc-start-${item.key}`} /></div>
        <StaffSelect item={item} line={line} branchId={branchId} onChange={(v) => onChange({ staff_id: v })} />
        <div className="sm:col-span-2"><Label className="text-xs">Notes for this service</Label>
          <Input value={item.notes} onChange={(e) => onChange({ notes: e.target.value })} placeholder="e.g. limping on left leg since morning" className="mt-1 h-10" data-testid={`add-svc-notes-${item.key}`} /></div>
      </div>
      {!!line?.conflicts?.length && (
        <div className="mt-2 rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-800" data-testid={`add-svc-conflict-${item.key}`}>
          <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />{line.staff_name} is busy: {line.conflicts.map((c) => `${c.service_name || c.kind} ${dayjs(c.start_time).format("h:mm")}–${dayjs(c.end_time).format("h:mm A")}`).join("; ")}. Pick another time or person, or leave unassigned.
        </div>
      )}
    </div>
  );
}

function Existing({ booking }) {
  const { data } = useQuery({ queryKey: ["assignments", booking.id], queryFn: async () => (await api.get(`/bookings/${booking.id}/assignments`)).data });
  const rows = (data?.items || []).filter((r) => r.status !== "cancelled");
  const lines = booking.services?.length ? booking.services : [{ service_name: booking.service_name }];
  return (
    <div className="rounded-xl bg-slate-50 p-3" data-testid="add-svc-existing">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Already on this booking</p>
      <ul className="mt-1.5 space-y-1">
        {lines.map((s, i) => {
          const r = rows.find((x) => x.service_id === s.service_id && (x.pet_id || "") === (s.pet_id || x.pet_id || ""));
          return (
            <li key={i} className="flex flex-wrap items-center justify-between gap-x-2 text-sm" data-testid={`add-svc-existing-${i}`}>
              <span className="min-w-0 break-words text-slate-800">{s.service_name}{r?.staff_names?.length ? <span className="text-slate-500"> · {r.staff_names.join(", ")}</span> : null}</span>
              <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-600">{r ? (r.status === "scheduled" && booking.status === "in_progress" ? (r.category === "vet" ? "Waiting for vet" : "Waiting") : ROW_STATUS[r.status] || r.status) : booking.status.replace("_", " ")}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function PriceBox({ p }) {
  if (!p) return null;
  const inv = p.billing?.mode === "additional_charge";
  return (
    <div className="rounded-xl border border-slate-200 p-3 text-sm" data-testid="add-svc-price">
      <div className="flex justify-between"><span className="text-slate-500">Current booking total</span><span className="tabular">{inr(p.current_total)}</span></div>
      <div className="flex justify-between"><span className="text-slate-500">Added services (incl. tax {inr(p.added_tax)})</span><span className="tabular font-semibold text-emerald-700" data-testid="add-svc-added-total">+{inr(p.added_total)}</span></div>
      <div className="mt-1 flex justify-between border-t border-slate-100 pt-1 font-semibold"><span>New booking total</span><span className="tabular" data-testid="add-svc-new-total">{inr(p.new_total)}</span></div>
      {inv ? <p className="mt-2 rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-800" data-testid="add-svc-invoice-note">Invoice {p.billing.invoice_number} ({p.billing.state.replace("_", " ").toLowerCase()}) is not changed automatically. {p.billing.state === "UNPAID" ? "After saving, add the charge to the invoice with the “not on invoice” button." : "Because it already has a payment, add the charge afterwards with Correct Invoice on the Invoices page."}</p>
        : <p className="mt-2 text-xs text-slate-400">{p.note} No payment is collected now.</p>}
    </div>
  );
}

export function AddServiceDialog({ booking, onClose }) {
  const qc = useQueryClient();
  const { data: services } = useQuery({ queryKey: ["services"], queryFn: async () => (await api.get("/services")).data });
  const pets = booking.pets?.length ? booking.pets : [{ id: booking.pet_id || "", name: booking.pet_name || "Pet" }];
  const [items, setItems] = useState([]);
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");
  const [conflict, setConflict] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const token = useRef(`add-${booking.id}-${Date.now()}`);
  const payload = items.map((i) => ({ service_id: i.service_id, pet_id: i.pet_id, qty: 1, staff_id: i.staff_id, start_time: ist(i.start), notes: i.notes }));
  const sig = JSON.stringify(payload);

  useEffect(() => {
    if (!items.length) { setPreview(null); setErr(""); return undefined; }
    const h = setTimeout(async () => {
      try { setPreview((await api.post(`/bookings/${booking.id}/add-services`, { items: payload, dry_run: true })).data); setErr(""); }
      catch (e) { setErr(apiErr(e)); setPreview(null); }
    }, 300);
    return () => clearTimeout(h);
  }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (s) => setItems((prev) => [...prev, { key: `${s.id}-${prev.length}-${Date.now() % 10000}`, service_id: s.id, name: s.name, category: s.category, pet_id: pets[0].id, staff_id: "", start: "", notes: "" }]);
  const patch = (key, p) => setItems((prev) => prev.map((i) => (i.key === key ? { ...i, ...p } : i)));
  const lineFor = (i) => preview?.lines?.[items.indexOf(i)];

  const save = async (override) => {
    setBusy(true);
    try {
      const r = (await api.post(`/bookings/${booking.id}/add-services`, { items: payload, client_token: token.current, override_reason: override ? reason : "" })).data;
      setDone(r); invalidate(qc);
      toast.success(`${items.length} service${items.length > 1 ? "s" : ""} added to the booking`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.conflicts) setConflict(d); else toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden grid-cols-[minmax(0,1fr)]" data-testid="add-service-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Add service</DialogTitle>
          <DialogDescription>{booking.customer_name}{booking.pet_name ? ` · ${booking.pet_name}` : ""} — new services join this booking and go to their department. Existing services and their staff stay as they are.</DialogDescription>
        </DialogHeader>
        {done ? <Done done={done} booking={booking} onClose={onClose} /> : (
          <div className="space-y-3">
            <Existing booking={booking} />
            <Catalogue services={services || []} picked={new Set(items.map((i) => i.service_id))} onBooking={new Set((booking.services || []).map((s) => s.service_id))} onPick={pick} />
            {items.map((i) => <ItemCard key={i.key} item={i} line={lineFor(i)} pets={pets} branchId={booking.branch_id} onChange={(p) => patch(i.key, p)} onRemove={() => setItems((prev) => prev.filter((x) => x.key !== i.key))} />)}
            {err && <p className="rounded-md bg-rose-50 px-2 py-1.5 text-xs text-rose-700" data-testid="add-svc-error">{err}</p>}
            <PriceBox p={preview?.pricing} />
            {conflict && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900" data-testid="add-svc-save-conflict">
                <div className="font-semibold"><AlertTriangle className="mr-1 inline h-3.5 w-3.5" />{conflict.msg}</div>
                {conflict.can_override ? (
                  <div className="mt-2 flex gap-2">
                    <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Override reason (required)" className="h-8 bg-white" data-testid="add-svc-override-reason" />
                    <Button size="sm" disabled={busy || !reason.trim()} onClick={() => save(true)} className="bg-amber-600 text-white hover:bg-amber-700" data-testid="add-svc-override-btn">Override & save</Button>
                  </div>
                ) : <div className="mt-1">Choose another time or staff member, leave the service unassigned, or ask a manager.</div>}
              </div>
            )}
          </div>
        )}
        {!done && (
          <DialogFooter>
            <Button variant="outline" onClick={onClose} data-testid="add-svc-cancel">Cancel</Button>
            <Button disabled={busy || !items.length || !!err} onClick={() => save(false)} className="bg-slate-900 text-white" data-testid="add-svc-save">{busy ? "Saving…" : `Add ${items.length || ""} service${items.length === 1 ? "" : "s"}`}</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Done({ done, booking, onClose }) {
  const b = done.booking || booking;
  return (
    <div className="space-y-3" data-testid="add-svc-done">
      <div className="flex items-start gap-2 rounded-xl bg-emerald-50 p-3 text-sm text-emerald-800">
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
        <div><div className="font-semibold">Added to this booking</div>
          <ul className="mt-1 space-y-0.5">{(done.added || []).map((l, i) => <li key={i} data-testid={`add-svc-done-${i}`}>{l.service_name} → {deptLabel(l.department)} · {l.staff_name || "unassigned"}{l.start_time ? ` · ${t(l.start_time)}` : ""}</li>)}</ul>
        </div>
      </div>
      {b.billing?.unbilled ? <div className="text-sm">Not yet on invoice {b.billing.invoice_number}: <UnbilledChip booking={b} /></div>
        : <p className="text-sm text-slate-500">New booking total: <b>{inr(done.pricing?.new_total)}</b>. Included when you bill this booking.</p>}
      <div className="flex justify-end"><Button onClick={onClose} className="bg-slate-900 text-white" data-testid="add-svc-close">Done</Button></div>
    </div>
  );
}
